import React, { useState } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  Button,
  FieldLabel,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { useIsMounted } from '@vend/react-utilities'
import { multiply, round } from 'vend-number'
import { useCashMovementsManager } from '../../../ng-compat/hooks'
import { wrToastNegative } from '../../react/helios/ToastNotifications/WrToastNotifications'
import { MovementVariant, CashFloatSettings, Movement } from '../utils/types'
import { CashFloatForm } from '../CashFloatForm'
import { CASH_MOVEMENT_TYPES, CASH_MOVEMENTS } from '../../../constants'
import { TypeSelector } from './TypeSelector'

interface Props {
  variant: MovementVariant
  onClose: () => void
  onSuccess: (cashMovement: Movement) => void
}

const RECORD_CASH_MOVEMENT_FAILED_TOAST_KEY = 'record.cash.movement.negative'

const getCashMovementAmount = (
  originalValue: string,
  variant: MovementVariant
) => {
  if (originalValue === '') {
    return '0.00'
  }
  if (variant === MovementVariant.Remove) {
    return round(multiply(originalValue, -1), 2).toString()
  }
  return originalValue
}

export const RecordMovementModal: React.FC<Props> = ({
  variant,
  onClose,
  onSuccess,
}) => {
  const t = useT()
  const isMountedRef = useIsMounted()
  const cashMovementsManager = useCashMovementsManager()

  const [selectedType, setSelectedType] = useState<CASH_MOVEMENT_TYPES>(
    CASH_MOVEMENTS[variant][0]
  )
  const [hasError, setHasError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const handleTypeSelect = (type: CASH_MOVEMENT_TYPES) => {
    setSelectedType(type)
  }
  const handleOnError = (hasError: boolean) => {
    setHasError(hasError)
  }
  const handleSubmit = (settings: CashFloatSettings) => {
    const cashMovement = {
      ...settings,
      amount: getCashMovementAmount(settings.amount, variant),
    }
    setLoading(true)
    cashMovementsManager
      .recordCashMovement(cashMovement)
      .then(cashMovement => {
        if (isMountedRef.current) {
          setLoading(false)
          onClose()
          onSuccess(cashMovement as Movement)
        }
      })
      .catch(() => {
        if (isMountedRef.current) {
          setLoading(false)
        }
        wrToastNegative(RECORD_CASH_MOVEMENT_FAILED_TOAST_KEY)
      })
  }
  return (
    <Modal
      size="small"
      onClose={onClose}
      dismissable={!loading}
      // focusTrapActive={false}
    >
      <ModalHeader>
        {variant === MovementVariant.Remove
          ? t('Remove cash', { _context: 'Modal header' })
          : t('Add cash', { _context: 'Modal header' })}
      </ModalHeader>
      <ModalContent>
        <FieldLabel label={t('Type', { _context: 'Field label' })}>
          <TypeSelector
            variant={variant}
            selectedType={selectedType}
            onSelect={handleTypeSelect}
          />
        </FieldLabel>
        <CashFloatForm
          type={selectedType}
          noteRows={1}
          onError={handleOnError}
          onSubmit={handleSubmit}
        >
          <div className="vd-align-right vd-btn-group vd-pr1">
            <Button
              variant="supplementary"
              onClick={onClose}
              disabled={loading}
            >
              {t('Cancel', { _context: 'Button' })}
            </Button>
            <Button
              type="submit"
              disabled={hasError || loading}
              variant={variant === MovementVariant.Remove ? 'no' : 'go'}
              data-testid={`${variant}-cash-btn`}
            >
              {variant === MovementVariant.Remove
                ? t('Remove cash', { _context: 'Button' })
                : t('Add cash', { _context: 'Button' })}
            </Button>
          </div>
        </CashFloatForm>
      </ModalContent>
    </Modal>
  )
}
